import { demandManagerRequestMagniteSlots, getGoogleTag, getPreBidJs } from 'components/ad-magnite/magnite-libs';
import { useEffect } from 'react';
import { AdComponent } from 'redux-state/advertisement/types.js';
import { PrebidMagniteSlotMapObject } from 'typings/magnite-prebid';
import { messageLog } from 'utilities/helpers/logger/message-logger';
import { getWindow } from 'utilities/window';
import { createBannerAdSlotRenderEndedCB, createInterstitialAdSlotLoadedCB, getTagSlot } from './utils/advert-utils';

export const AD_REQUEST = String.fromCodePoint(0x27a1); // ➡️
export const AD_RESPONSE_HAS_AD = String.fromCodePoint(0x1f7e2); // 🟢
export const AD_RESPONSE_NO_AD = String.fromCodePoint(0x26aa); // ⚪
export const AD_DESTROY = String.fromCodePoint(0x274c); // ❌

type AdvertComponentProps = AdComponent<{
  origin: string;
  timerDOMReady?: (f: boolean) => void;
  timerAdRequest?: (f: boolean) => void;
  timerAdResponse?: (f: boolean) => void;
  adDisplayTrueCB?: () => void;
  adDisplayFalseCB?: () => void;
}>;

/**  Builds dom element based on ad props */
export const AdvertComponent = ({
  origin,
  id,
  def,
  sizes,
  targeting,
  ac,
  timerDOMReady,
  timerAdRequest,
  timerAdResponse,
  adDisplayTrueCB,
  adDisplayFalseCB,
}: AdvertComponentProps) => {
  const forbiddenTerms = ['inst', 'map', 'til', 'nav', 'wgt'];
  const preventPrebid = forbiddenTerms.some((term) => def.includes(term));
  const playwrightSettings = getWindow()?.playwright;
  const hideAd = playwrightSettings?.isPlaywright && playwrightSettings?.maskAds;
  const isInterstitial = origin === 'interstitial';

  useEffect(() => {
    if (hideAd) return;
    const slotTargeting = (targeting ?? []).reduce<Record<string, string[]>>((acc, { key, value }) => {
      if (key && value) {
        acc[key] = [`${value}`];
      }
      return acc;
    }, {});

    const adUnitPath = `/${ac}${def}`;

    messageLog.ads(`Request ${origin}: ${AD_REQUEST}`, {
      args: [['args', { def: adUnitPath, sizes, targeting, id, preventPrebid }]],
      id,
    });
    let slot: googletag.Slot | undefined;

    const bannerAdSlotRenderEndedCB = createBannerAdSlotRenderEndedCB({ adUnitPath, origin, id, timerAdResponse, adDisplayTrueCB, adDisplayFalseCB });
    const interstitialAdSlotLoadedCB = createInterstitialAdSlotLoadedCB({ adUnitPath, origin, id });

    const googletag = getGoogleTag();
    googletag.cmd.push(() => {
      slot = getTagSlot({ isInterstitial, adUnitPath, sizes, id, slotTargeting });
      if (isInterstitial) {
        googletag.pubads().addEventListener("slotOnload", interstitialAdSlotLoadedCB);
      }
      else {
        googletag.pubads().addEventListener('slotRenderEnded', bannerAdSlotRenderEndedCB);
      }
      timerAdRequest?.(true);

      if (slot) {
        googletag.display(slot);
        if (preventPrebid) {
          // we are not using prebid so just refresh the slot to load the ad.
          googletag.pubads().refresh([slot]);
        }
      }
    });

    if (!preventPrebid) {
      const pbjs = getPreBidJs();
      // wait for pbjs to load
      pbjs.que.push(function () {
        // build a slot map which can be mapped to any relevant GPT header bidding slots
        const slotMap: PrebidMagniteSlotMapObject[] = [
          { name: adUnitPath, divId: id, sizes: sizes.map((s) => ({ w: s[0], h: s[1] })) },
        ];
        // make a request to DM to start header bidding
        demandManagerRequestMagniteSlots(slotMap, pbjs);
      });
    }

    return () => {
      googletag.cmd.push(() => {
        googletag.pubads().removeEventListener('slotRenderEnded', bannerAdSlotRenderEndedCB);
        googletag.pubads().removeEventListener('slotOnload', interstitialAdSlotLoadedCB);
        if (slot) {
          messageLog.ads(`Clear ${origin}${AD_DESTROY}`, {
            id,
          });
          googletag.destroySlots([slot]);
        }
      });
    };
  }, [
    id,
    def,
    sizes,
    targeting,
    ac,
    adDisplayTrueCB,
    adDisplayFalseCB,
    origin,
    timerAdRequest,
    timerAdResponse,
    preventPrebid,
    hideAd,
    isInterstitial
  ]);

  useEffect(() => {
    // Runs only once after the initial render so dom is ready on screen
    timerDOMReady?.(true);
    return () => {
      timerDOMReady?.(false);
    };
  }, [timerDOMReady]);

  if (hideAd || isInterstitial) {
    return null;
  }

  return <div id={id} />;
};
